/**
 * PRICE LISTS, WHO IS ON THEM, AND WHAT A SWITCH WOULD CHANGE.
 *
 * Three things the screen refuses to blur:
 *
 * An ACTIVE list is read only. It is what customers are being charged from, and
 * a price edited underneath a live agreement is a price nobody agreed to. To
 * change one you draft its successor, price it, and activate that — the lineage
 * stays visible, so "what were we charging in June" has an answer.
 *
 * The RECONCILIATION is per customer and it is complete. It shows every legacy
 * identifier that customer is priced on today, the SKU it maps to, both prices
 * and the difference — including the ones with no mapping and no price, which
 * are the whole reason to look. The screen does not offer a way to see a
 * convenient subset and call it done: the counts at the top are of everything.
 *
 * A SWITCH is refused while anything is unmapped or unpriced, and refused again
 * if it would silently change what a customer pays. That last refusal can be
 * overridden — deliberately, in writing — and never by a checkbox nobody reads.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session, type Views } from '../api.js';
import { useAsk, useRun } from '../hooks.js';
import { Banner, Button, Card, Check, Loading, Money, Pill, Register, Select, Text, TextArea, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { CustomerPickerById } from '../pickers.js';
import { count, date, money, rupeesToPaise, today, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('prices')!.messages;
type Tab = 'agreed' | 'lists' | 'assign' | 'reconcile';

/**
 * RATES — and the first question it has to answer is WHICH RATES PRICE A
 * DISPATCH HERE (12 October, the owner: "this page is incomplete and
 * comfusing").
 *
 * There are two ways to price in this system and the screen showed only one.
 * A dispatch is priced from the CUSTOMER'S AGREED CARD — the gas rates, the
 * holding tiers and the delivery charges on their own page, written by an
 * approved quotation (0174) — unless the company has declared the CATALOGUE
 * mode, where a price list does it instead. Every company starts on the cards.
 *
 * So the page opened on "Price lists", showed "No price list matches that."
 * to a company that has none and needs none, said "SKU" twice in a sentence
 * the owner had already struck, and never mentioned the rates that actually
 * priced yesterday's load. It now leads with those.
 */
export function Prices({ id, session }: { id: string | null; session: Session }) {
  const [tab, setTab] = useState<Tab>('agreed');
  if (id) return <OnePriceList priceListId={Number(id)} session={session} />;
  return (
    <Page title="Rates" sub="What each customer pays, and the price lists for companies that work from a catalogue.">
      <div className="segmented">
        <button type="button" className={tab === 'agreed' ? 'on' : ''} onClick={() => setTab('agreed')}>What each customer pays</button>
        <button type="button" className={tab === 'lists' ? 'on' : ''} onClick={() => setTab('lists')}>Price lists</button>
        <button type="button" className={tab === 'assign' ? 'on' : ''} onClick={() => setTab('assign')}>Who is on which list</button>
        <button type="button" className={tab === 'reconcile' ? 'on' : ''} onClick={() => setTab('reconcile')}>Cutover check</button>
      </div>
      {tab === 'agreed' ? <AgreedRates session={session} />
        : tab === 'lists' ? <Lists session={session} />
        : tab === 'assign' ? <Assign session={session} /> : <Reconcile session={session} />}
    </Page>
  );
}

/** The rates a dispatch is actually priced from, for every customer on the books. */
function AgreedRates({ session }: { session: Session }) {
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('rates.agreed', { ...(q.trim() ? { q: q.trim() } : {}), page, pageSize: 50 }), [q, page], MESSAGES);
  const mode = useAsk(() => api.view('pricing.mode', {}), [], MESSAGES);
  const onCatalogue = (mode.data?.rows ?? []).some((r) => r.source === 'catalogue' && r.effectiveTo === null);

  type Row = Views['rates.agreed']['result']['rows'][number];
  const columns: readonly Column<Row>[] = [
    { key: 'who', head: 'Customer', cell: (r) => <a href={`#/customers/${r.customerId}`}>{r.customerName}</a> },
    { key: 'gas', head: 'Gas rates', cell: (r) => {
      const gases = Object.entries(r.gasRates);
      if (gases.length === 0) return <Pill tone="danger">no rates agreed</Pill>;
      return <>{gases.map(([g, paise]) => <span key={g} className="rate-chip">{words(g)} <b>{money(paise)}</b>/cyl</span>)}</>;
    } },
    { key: 'hold', head: 'Holding', cell: (r) => r.holdingFreeDays === null
      ? <span className="muted">—</span>
      : <>{count(r.holdingFreeDays)} free days{Array.isArray(r.holdingSlabs) ? ` · ${count((r.holdingSlabs as unknown[]).length)} tier(s)` : ''}</> },
    { key: 'carry', head: 'Delivery', right: true, cell: (r) => r.freightPaise === null
      ? <span className="muted">not agreed</span>
      : <>{money(r.freightPaise)}/trip</> },
    { key: 'gov', head: 'Governed by', cell: (r) => r.quotationId
      ? <><a href={`#/quotes/${r.quotationId}`}>offer #{r.quotationId}</a>
          <div className="muted">{r.autoRenew ? 'renewing monthly' : r.validUntil ? `until ${date(r.validUntil)}` : 'no end date'}</div></>
      : <span className="muted">entered by hand</span> },
    { key: 'state', head: '', right: true, cell: (r) => r.missing
      ? <Pill tone="danger">no card</Pill> : r.lapsed ? <Pill tone="testing">lapsed</Pill> : <Pill tone="good">in force</Pill> },
  ];

  return (
    <>
      <Card>
        <p style={{ margin: 0 }}>
          {onCatalogue
            ? 'This company prices from the CATALOGUE: a dispatch takes its rate from the price list the customer is on. The cards below are what each customer was last agreed at.'
            : 'A dispatch is priced from the customer’s own agreed card — these rates. Price lists are the other way of doing it, and this company is not on them.'}
        </p>
        <p className="muted" style={{ marginBottom: 0 }}>
          A card is changed on the customer’s own page, or by approving a quotation that says
          “save these rates to the customer’s card” — which is how a rate change leaves a paper trail.
        </p>
      </Card>
      <Card title="What each customer pays" sub="the rates in force today, per customer">
        <div className="toolbar">
          <div className="field grow"><label htmlFor="rates-q">Search</label>
            <input id="rates-q" type="search" value={q} placeholder="Customer name"
                   onChange={(e: { target: { value: string } }) => { setPage(1); setQ(e.target.value); }} /></div>
        </div>
        {asked.loading ? <Loading what="the rates" /> : asked.error
          ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows}
                        empty="Nobody is on the books yet. Add a customer and their rates are agreed on their own page." />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
      {can(session, 'price.manage') ? null : <p className="muted">Changing a rate needs the pricing permission.</p>}
    </>
  );
}

function Lists({ session }: { session: Session }) {
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('pricelists.list', { ...(status ? { status } : {}), page, pageSize: 50 }), [status, page], MESSAGES);
  const [drafting, setDrafting] = useState(false);
  const may = can(session, 'price.manage');

  const columns: readonly Column<{ priceListId: number; code: string; name: string; status: string; effectiveFrom: string; effectiveTo: string | null; supersedesId: number | null; itemCount: number }>[] = [
    { key: 'code', head: 'Code', cell: (r) => <strong>{r.code}</strong> },
    { key: 'name', head: 'Name', cell: (r) => r.name },
    { key: 'status', head: 'Status', cell: (r) => <Pill tone={r.status === 'active' ? 'good' : r.status === 'draft' ? 'empty' : 'muted'}>{r.status}</Pill> },
    { key: 'from', head: 'In force', cell: (r) => <>{date(r.effectiveFrom)}{r.effectiveTo ? ` — ${date(r.effectiveTo)}` : ''}</> },
    { key: 'items', head: 'Prices', right: true, cell: (r) => count(r.itemCount) },
    { key: 'sup', head: 'Supersedes', cell: (r) => r.supersedesId ? <a href={`#/prices/${r.supersedesId}`}>#{r.supersedesId}</a> : <span className="muted">—</span> },
  ];

  return (
    <>
      <div className="toolbar">
        <Select name="status" label="Status" value={status} onChange={(v) => { setStatus(v); setPage(1); }} placeholder="Any status"
                options={[{ value: 'draft', label: 'Draft' }, { value: 'active', label: 'Active' }, { value: 'superseded', label: 'Superseded' }]} />
        {may ? <Button kind="primary" onClick={() => setDrafting((v) => !v)}>{drafting ? 'Cancel' : 'Draft a list'}</Button> : null}
      </div>
      {drafting ? <DraftList lists={asked.data?.rows ?? []} onDone={() => { setDrafting(false); asked.reload(); }} /> : null}
      <Card>
        {asked.loading ? <Loading what="price lists" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows} empty="No price list matches that."
                        onOpen={(r) => { globalThis.location.hash = `#/prices/${r.priceListId}`; }} />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </>
  );
}

function DraftList({ lists, onDone }: { lists: readonly { priceListId: number; code: string; status: string }[]; onDone: () => void }) {
  const [code, setCode] = useState(''); const [name, setName] = useState('');
  const [from, setFrom] = useState(today()); const [supersedes, setSupersedes] = useState(''); const [note, setNote] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(async () => {
      const r = await api.command('price.draftList', {
        code: code.trim(), name: name.trim(), effectiveFrom: from,
        ...(supersedes ? { supersedesId: Number(supersedes) } : {}),
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      const v = r.value as { priceListId?: number };
      if (v?.priceListId) globalThis.location.hash = `#/prices/${v.priceListId}`;
      return r;
    });
    if (ok) onDone();
  }
  return (
    <Card title="Draft a price list" sub="a draft is priced and then activated — nothing is charged from it until it is">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div><Text name="list-code" label="Code" value={code} onChange={setCode} required autoFocus />
             <Text name="list-name" label="Name" value={name} onChange={setName} required /></div>
        <div><Text name="list-from" label="Effective from" type="date" value={from} onChange={setFrom} required />
             <Select name="list-supersedes" label="Supersedes" value={supersedes} onChange={setSupersedes} placeholder="Nothing — this is a new list"
                     options={lists.filter((l) => l.status === 'active').map((l) => ({ value: String(l.priceListId), label: l.code }))} /></div>
      </div>
      <TextArea name="list-note" label="Note" value={note} onChange={setNote} />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !code.trim() || !name.trim()}>Draft it</Button>
    </Card>
  );
}

function OnePriceList({ priceListId, session }: { priceListId: number; session: Session }) {
  const asked = useAsk(() => api.view('pricelists.get', { priceListId }), [priceListId], MESSAGES);
  const skus = useAsk(() => api.view('skus.list', { active: true, pageSize: 200 }), [], MESSAGES);
  const runner = useRun(MESSAGES);
  const [skuId, setSkuId] = useState(''); const [rupees, setRupees] = useState('');
  const [activating, setActivating] = useState(false); const [reason, setReason] = useState('');

  if (asked.loading) return <Page title="Price list"><Loading what="the list" /></Page>;
  if (asked.error) return <Page title="Price list"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const d = asked.data!;
  const draft = d.list.status === 'draft';
  const may = can(session, 'price.manage');
  const paise = rupees.trim() ? rupeesToPaise(rupees) : null;

  async function setPrice() {
    const ok = await runner.run(() => api.command('price.set', { priceListId, skuId: Number(skuId), pricePaise: paise! }));
    if (ok) { setSkuId(''); setRupees(''); asked.reload(); }
  }
  async function activate() {
    const ok = await runner.run((retryKey) => api.command('price.activateList', { priceListId, reason: reason.trim() }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setActivating(false); asked.reload(); }
  }

  return (
    <Page title={`${d.list.code} — ${d.list.name}`}
          sub={`${words(d.list.status)} · in force from ${date(d.list.effectiveFrom)}${d.list.effectiveTo ? ` to ${date(d.list.effectiveTo)}` : ''} · ${count(d.list.itemCount)} price(s)`}
          actions={draft && may ? <Button kind="primary" onClick={() => setActivating((v) => !v)}>{activating ? 'Leave it a draft' : 'Activate it'}</Button> : <Pill tone={d.list.status === 'active' ? 'good' : 'muted'}>{d.list.status}</Pill>}>

      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}

      {!draft ? (
        <Banner kind="info" title="This list is not a draft, so it cannot be edited."
                detail="It is what somebody is being charged from. To change a price, draft a list that supersedes this one — the old list stays readable, and June's prices are still June's prices." />
      ) : null}

      {activating ? (
        <Card title="Activate this list" sub="from the effective date, this is what customers on it are charged">
          <TextArea name="activate-reason" label="Why it is being activated" value={reason} onChange={setReason} />
          <Button kind="primary" onClick={() => { void activate(); }} disabled={runner.busy || !reason.trim()}>
            {runner.busy ? 'Activating…' : 'Activate it'}
          </Button>
        </Card>
      ) : null}

      {draft && may ? (
        <Card title="Price a SKU" sub="setting a price that is already there replaces it — while this is a draft">
          {paise === null && rupees.trim() ? <Banner kind="warn" title="That is not money." detail="Rupees, with at most two decimals." /> : null}
          <div className="toolbar">
            <Select name="price-sku" label="SKU" value={skuId} onChange={setSkuId} placeholder="Choose a SKU"
                    options={(skus.data?.rows ?? []).map((s) => ({ value: String(s.skuId), label: `${s.code} — ${s.gasCode}/${s.gradeCode}/${s.sizeCode}` }))} />
            <Text name="price-rupees" label="Price (₹)" value={rupees} onChange={setRupees} />
            <Button kind="primary" onClick={() => { void setPrice(); }} disabled={runner.busy || !skuId || paise === null}>Set it</Button>
          </div>
        </Card>
      ) : null}

      <Card title="What is priced" sub="a SKU with no price on the active list is a SKU nobody can be charged for">
        <Register
          columns={[
            { key: 'sku', head: 'SKU', cell: (i: { sku: string }) => <strong>{i.sku}</strong> },
            { key: 'price', head: 'Price', right: true, cell: (i: { pricePaise: number }) => <Money paise={i.pricePaise} /> },
          ]}
          rows={d.items} empty="Nothing is priced on this list yet."
          totals={<span><b>{count(d.items.length)}</b> priced of <b>{count(skus.data?.page.total ?? 0)}</b> active SKU(s)</span>} />
      </Card>
    </Page>
  );
}

function Assign({ session }: { session: Session }) {
  const lists = useAsk(() => api.view('pricelists.list', { status: 'active', pageSize: 100 }), [], MESSAGES);
  const runner = useRun(MESSAGES);
  const [customerId, setCustomerId] = useState(''); const [priceListId, setPriceListId] = useState('');
  const [from, setFrom] = useState(today()); const [to, setTo] = useState(''); const [reason, setReason] = useState('');
  const may = can(session, 'price.manage');

  async function submit() {
    const ok = await runner.run((retryKey) => api.command('price.assign', {
      customerId: Number(customerId), priceListId: Number(priceListId), effectiveFrom: from, reason: reason.trim(),
      ...(to ? { effectiveTo: to } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setCustomerId(''); setPriceListId(''); setReason(''); }
  }

  return (
    <>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {!may ? <Banner kind="info" title="You can read this, and not change it." /> : null}
      <Card title="Put a customer on a price list" sub="an order already approved keeps the rates it was approved on — this applies from the date below">
        <div className="grid two">
          <div>
            <CustomerPickerById name="assign-customer" label="Customer" customerId={customerId}
                                onPick={(v) => setCustomerId(v)} placeholder="Choose a customer" />
            <Select name="assign-list" label="Price list" value={priceListId} onChange={setPriceListId} placeholder="Choose an active list"
                    options={(lists.data?.rows ?? []).map((l) => ({ value: String(l.priceListId), label: `${l.code} — ${l.name}` }))} />
          </div>
          <div>
            <Text name="assign-from" label="From" type="date" value={from} onChange={setFrom} required />
            <Text name="assign-to" label="To" type="date" value={to} onChange={setTo} help="leave blank while it is open-ended" />
            <TextArea name="assign-reason" label="Why" value={reason} onChange={setReason} />
          </div>
        </div>
        <Button kind="primary" onClick={() => { void submit(); }} disabled={!may || runner.busy || !customerId || !priceListId || !reason.trim()}>
          {runner.busy ? 'Assigning…' : 'Assign it'}
        </Button>
      </Card>

      <OnWhichList session={session} onChanged={() => { /* the card reloads itself */ }} />
    </>
  );
}

/**
 * WHICH LIST EACH CUSTOMER IS ON — AND THE BUTTON THAT ENDS A PERIOD.
 *
 * Two gaps met here, and the second was hidden by the first.
 *
 * The screen could put a customer on a price list and could not show which one they were
 * on. That is half a feature in the direction that matters least: assigning is the rare act
 * and looking it up is the daily one, and nobody about to change a customer's rates should
 * have to ask the database which rates they are on.
 *
 * And nothing could END a period. `price.assign` refuses an overlap with "close that period
 * first" and, until 0133, there was no command that closed one — so a customer put on a list
 * "until further notice", which is what leaving the To box blank gives you, could never be
 * moved to another list again. The refusal told the operator to do something the product did
 * not have. It was invisible from here precisely BECAUSE the periods were invisible: you
 * cannot notice a missing button for ending something you cannot see.
 *
 * The button sits on the row it affects, not in a form of its own, because "end THIS period"
 * is about a period the operator is looking at. Rows that cannot be closed say why in words
 * rather than showing a disabled control with no explanation.
 */
function OnWhichList({ session, onChanged }: { session: Session; onChanged: () => void }) {
  const [who, setWho] = useState('');
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('priceassign.list', {
    page, pageSize: 25, ...(who ? { customerId: Number(who) } : {}),
  }), [page, who], MESSAGES);
  const [closing, setClosing] = useState<number | null>(null);
  const [endsOn, setEndsOn] = useState(today());
  const [why, setWhy] = useState('');
  const runner = useRun(MESSAGES);
  const may = can(session, 'price.close_assignment');

  async function close(assignmentId: number) {
    const ok = await runner.run((retryKey) => api.command('price.closeAssignment', {
      assignmentId, effectiveTo: endsOn, reason: why.trim(),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) { setClosing(null); setWhy(''); asked.reload(); onChanged(); }
  }

  return (
    <Card title="Who is on which list" sub="and until when — a period left open has to be ended before the customer can move to another list">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="filters">
        <CustomerPickerById name="pa-customer" label="Customer" customerId={who}
                            onPick={(v) => { setWho(v); setPage(1); }} placeholder="Everyone" />
      </div>
      {asked.loading && !asked.data ? <Loading what="the price-list periods" /> : null}
      {asked.error ? <Banner kind="warn" title={asked.error.title} /> : null}
      {asked.data ? (
        <>
          <Register
            columns={[
              { key: 'who', head: 'Customer', cell: (r) => <strong>{r.customer}</strong> },
              { key: 'list', head: 'Price list', cell: (r) => <>{r.code} <span className="muted">{r.name}</span></> },
              { key: 'from', head: 'From', cell: (r) => date(r.effectiveFrom) },
              { key: 'to', head: 'Until', cell: (r) => r.effectiveTo
                  ? date(r.effectiveTo)
                  : <Pill tone="empty">further notice</Pill> },
              { key: 'why', head: 'Why', cell: (r) => <span className="muted">{r.reason}</span> },
              { key: 'do', head: 'What can be done', cell: (r) => {
                  // A row that cannot be ended SAYS SO. A disabled button with no sentence
                  // beside it leaves an operator clicking and guessing, which is the thing
                  // this screen was already doing by showing no periods at all.
                  if (!r.closable) return <span className="muted">ends {date(r.effectiveTo)} — a period with an end date is not re-ended</span>;
                  if (!may) return <span className="muted">ending a period needs a permission you do not hold</span>;
                  if (closing === r.assignmentId) {
                    return (
                      <div className="stack">
                        <Text name={`pa-to-${r.assignmentId}`} label="Ends on" type="date" value={endsOn} onChange={setEndsOn} required
                              help="today or later — bills already raised used these prices" />
                        <TextArea name={`pa-why-${r.assignmentId}`} label="Why they come off it" value={why} onChange={setWhy} />
                        <div className="toolbar">
                          <Button kind="primary" onClick={() => { void close(r.assignmentId); }}
                                  disabled={runner.busy || !endsOn || !why.trim()}>
                            {runner.busy ? 'Ending…' : 'End this period'}
                          </Button>
                          <Button kind="ghost" onClick={() => { setClosing(null); setWhy(''); }}>Leave it open</Button>
                        </div>
                      </div>
                    );
                  }
                  return <Button kind="ghost" onClick={() => { setClosing(r.assignmentId); setEndsOn(today()); setWhy(''); }}>End this period…</Button>;
                } },
            ] as Column<{ assignmentId: number; customer: string; code: string; name: string; effectiveFrom: string; effectiveTo: string | null; reason: string; closable: boolean }>[]}
            rows={asked.data.rows}
            empty={who ? 'This customer is on no price list, so nothing prices their orders from the catalogue.' : 'No customer is on a price list yet.'} />
          <Pager page={asked.data.page} onPage={setPage} />
        </>
      ) : null}
    </Card>
  );
}

// ---------------------------------------------------------------- the reconciliation
function Reconcile({ session }: { session: Session }) {
  const mode = useAsk(() => api.view('pricing.mode', {}), [], MESSAGES);
  const [customerId, setCustomerId] = useState('');
  const [on, setOn] = useState(today());
  const asked = useAsk(() => (customerId ? api.view('pricing.reconcile', { customerId: Number(customerId), on }) : Promise.resolve(null)), [customerId, on], MESSAGES);
  const may = can(session, 'pricing.switch');

  const current = mode.data?.rows.find((r) => !r.effectiveTo) ?? mode.data?.rows[mode.data.rows.length - 1];

  return (
    <>
      <Card title="How this company is priced today" sub="legacy identifiers, or the catalogue — and since when">
        {mode.loading ? <Loading what="the pricing mode" /> : mode.error ? <Banner kind="warn" title={mode.error.title} /> : (
          <>
            <div className="detail-grid">
              <div><div className="k">Priced from</div><div className="v"><Pill tone={current?.source === 'catalogue' ? 'good' : 'empty'}>{current?.source ?? 'unknown'}</Pill></div></div>
              <div><div className="k">Since</div><div className="v">{date(current?.effectiveFrom ?? null)}</div></div>
              <div><div className="k">Because</div><div className="v">{current?.reason ?? '—'}</div></div>
            </div>
            {mode.data!.rows.length > 1 ? (
              <Register
                columns={[
                  { key: 'src', head: 'Priced from', cell: (r: { source: string }) => words(r.source) },
                  { key: 'from', head: 'From', cell: (r: { effectiveFrom: string }) => date(r.effectiveFrom) },
                  { key: 'to', head: 'To', cell: (r: { effectiveTo: string | null }) => r.effectiveTo ? date(r.effectiveTo) : <span className="muted">still</span> },
                  { key: 'why', head: 'Why', cell: (r: { reason: string }) => r.reason },
                ]}
                rows={mode.data!.rows} empty="none" />
            ) : null}
          </>
        )}
      </Card>

      <div className="toolbar">
        <CustomerPickerById name="rec-customer" label="Reconcile which customer" customerId={customerId}
                            onPick={(v) => setCustomerId(v)} placeholder="Choose a customer" />
        <Text name="rec-on" label="As at" type="date" value={on} onChange={setOn} />
      </div>

      {!customerId ? (
        <Card><p className="muted" style={{ marginBottom: 0 }}>Choose a customer to see every legacy identifier they are priced on, the SKU it maps to, and what would change.</p></Card>
      ) : asked.loading ? <Card><Loading what="the reconciliation" /></Card>
        : asked.error ? <Card><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>
        : <ReconcileResult data={asked.data!} customerId={Number(customerId)} may={may} onChanged={() => asked.reload()} />}
    </>
  );
}

function ReconcileResult({ data, customerId, may, onChanged }: {
  data: { on: string; source: string; unmapped: number; unpriced: number; different: number; same: number;
          rows: readonly { kind: string; legacyKey: string; legacyPaise: number; skuId: number | null; skuCode: string | null; cataloguePaise: number | null; differencePaise: number | null; verdict: string }[] };
  customerId: number; may: boolean; onChanged: () => void;
}) {
  const [mapping, setMapping] = useState<string | null>(null);
  const skus = useAsk(() => api.view('skus.list', { active: true, pageSize: 200 }), [], MESSAGES);
  const blocked = data.unmapped > 0 || data.unpriced > 0;

  return (
    <>
      <div className="kpis">
        <div className="kpi" style={{ ['--accent' as string]: 'var(--danger)' }}>
          <div className="kpi-label">No SKU</div><div className="kpi-value">{count(data.unmapped)}</div><div className="kpi-note">legacy codes mapped to nothing</div>
        </div>
        <div className="kpi" style={{ ['--accent' as string]: 'var(--empty)' }}>
          <div className="kpi-label">No price</div><div className="kpi-value">{count(data.unpriced)}</div><div className="kpi-note">mapped, but unpriced on the active list</div>
        </div>
        <div className="kpi" style={{ ['--accent' as string]: 'var(--money)' }}>
          <div className="kpi-label">Price would change</div><div className="kpi-value">{count(data.different)}</div><div className="kpi-note">they would pay something else</div>
        </div>
        <div className="kpi" style={{ ['--accent' as string]: 'var(--good)' }}>
          <div className="kpi-label">Identical</div><div className="kpi-value">{count(data.same)}</div><div className="kpi-note">same price either way</div>
        </div>
      </div>

      {blocked ? (
        <Banner kind="warn" title="This customer cannot be switched yet."
                detail={`${count(data.unmapped)} legacy code(s) map to no SKU and ${count(data.unpriced)} mapped SKU(s) have no price. The command refuses both, and it is right to: a switch with a hole in it prices something at nothing.`} />
      ) : data.different > 0 ? (
        <Banner kind="warn" title="Switching would change what this customer pays."
                detail={`${count(data.different)} line(s) differ. The command refuses this too unless somebody says, in writing, that the change is intended.`} />
      ) : (
        <Banner kind="good" title="Every line maps, every SKU is priced, and no price changes." detail="This customer can be switched without anybody paying anything different." />
      )}

      <Card title="Every line, including the ones with nothing behind them"
            sub="this is the whole of what this customer is priced on — not a selection">
        <Register
          columns={[
            { key: 'kind', head: 'Kind', cell: (r: { kind: string }) => words(r.kind) },
            { key: 'legacy', head: 'Legacy code', cell: (r: { legacyKey: string }) => <strong>{r.legacyKey}</strong> },
            { key: 'was', head: 'Charged today', right: true, cell: (r: { legacyPaise: number }) => <Money paise={r.legacyPaise} /> },
            { key: 'sku', head: 'Maps to', cell: (r: { skuCode: string | null }) => r.skuCode ?? <Pill tone="danger">nothing</Pill> },
            { key: 'would', head: 'Would be', right: true, cell: (r: { cataloguePaise: number | null }) => r.cataloguePaise === null ? <Pill tone="empty">no price</Pill> : <Money paise={r.cataloguePaise} /> },
            { key: 'diff', head: 'Difference', right: true, cell: (r: { differencePaise: number | null }) => r.differencePaise === null
                ? <span className="muted">—</span>
                : r.differencePaise === 0 ? <span className="muted">none</span>
                : <strong style={{ color: r.differencePaise > 0 ? 'var(--danger)' : 'var(--good)' }}>{money(r.differencePaise)}</strong> },
            { key: 'verdict', head: 'Verdict', cell: (r: { verdict: string }) => <Pill tone={r.verdict === 'same' ? 'good' : r.verdict === 'DIFFERENT' ? 'money' : 'danger'}>{r.verdict}</Pill> },
            { key: 'map', head: '', cell: (r: { legacyKey: string; kind: string; skuId: number | null }) => may && r.skuId === null
                ? <Button small onClick={() => setMapping((v) => v === r.legacyKey ? null : r.legacyKey)}>{mapping === r.legacyKey ? 'Close' : 'Map it'}</Button> : null },
          ]}
          rows={data.rows} empty="This customer is priced on nothing at all." asOf={data.on} />
      </Card>

      {mapping !== null ? (
        <MapLegacy legacyKey={mapping} kind={data.rows.find((r) => r.legacyKey === mapping)?.kind ?? 'item_code'}
                   skus={skus.data?.rows ?? []} onDone={() => { setMapping(null); onChanged(); }} />
      ) : null}

      {may ? <Switch customerId={customerId} blocked={blocked} different={data.different} onDone={onChanged} /> : null}
    </>
  );
}

function MapLegacy({ legacyKey, kind, skus, onDone }: {
  legacyKey: string; kind: string;
  skus: readonly { skuId: number; code: string; gasCode: string; gradeCode: string; sizeCode: string | null }[];
  onDone: () => void;
}) {
  const [skuId, setSkuId] = useState(''); const [note, setNote] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run(() => api.command('pricing.map', {
      kind: kind === 'gas_service' ? 'gas_service' : 'item_code', legacyKey, skuId: Number(skuId),
      ...(note.trim() ? { note: note.trim() } : {}),
    }));
    if (ok) onDone();
  }
  return (
    <Card title={`Map “${legacyKey}” to a SKU`} sub="what the old system called it, and what it is in the catalogue">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <Select name="map-sku" label="SKU" value={skuId} onChange={setSkuId} placeholder="Choose the SKU"
              options={skus.map((s) => ({ value: String(s.skuId), label: `${s.code} — ${s.gasCode}/${s.gradeCode}/${s.sizeCode}` }))} />
      <TextArea name="map-note" label="Note" value={note} onChange={setNote} />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !skuId}>Map it</Button>
    </Card>
  );
}

function Switch({ customerId, blocked, different, onDone }: { customerId: number; blocked: boolean; different: number; onDone: () => void }) {
  const [from, setFrom] = useState(today()); const [reason, setReason] = useState('');
  const [accept, setAccept] = useState(false);
  const runner = useRun(MESSAGES);
  const needsDeliberateWords = different > 0;
  const said = reason.trim().length >= (needsDeliberateWords ? 25 : 8);
  // EVERY active customer is checked, not the one on screen. The command takes
  // the list it is to reconcile against and refuses an empty one, which is the
  // whole point: a switch checked against a convenient subset is not a check.
  const everyone = useAsk(() => api.view('customers.list', { active: true, pageSize: 200 }), [], MESSAGES);
  const ids = (everyone.data?.rows ?? []).map((c) => c.customerId);
  const partial = (everyone.data?.page.total ?? 0) > ids.length;

  async function submit() {
    const ok = await runner.run((retryKey) => api.command('pricing.switch', {
      source: 'catalogue', effectiveFrom: from, reason: reason.trim(),
      checkCustomers: ids,
      ...(needsDeliberateWords && accept ? { acceptPriceChanges: true } : {}),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) onDone();
  }

  return (
    <Card title="Move this company onto the catalogue"
          sub="the switch is company-wide — the reconciliation above is how you check one customer at a time before making it">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}
      {blocked ? (
        <Banner kind="error" title="Not while anything is unmapped or unpriced." detail="Close the holes above first. The command refuses this and so does the screen." />
      ) : needsDeliberateWords ? (
        <Banner kind="warn" title="Say what the price change is for."
                detail={`${count(different)} line(s) would be charged differently. Write down why that is intended — a sentence a person can read in six months, not "switching".`} />
      ) : null}
      {partial ? (
        <Banner kind="error" title="This company has more customers than can be reconciled in one call."
                detail={`${count(everyone.data!.page.total)} customers, and the command takes 200. Reconcile and switch in batches, or raise the limit — it will not be quietly truncated here.`} />
      ) : null}
      <p className="muted">
        Every one of this company's <b>{count(ids.length)}</b> active customers is reconciled by the command before it switches
        anything — not the one shown above. It refuses an empty list, and it refuses a list that still has a hole in it.
      </p>
      <Text name="switch-from" label="Effective from" type="date" value={from} onChange={setFrom} required />
      <TextArea name="switch-reason" label="Why the company is switching" value={reason} onChange={setReason} />
      {needsDeliberateWords ? (
        <Check name="accept-changes" checked={accept} onChange={setAccept}
               label={`Yes — I intend the price change on ${count(different)} line(s), and have said why above`} />
      ) : null}
      <Button kind="primary" onClick={() => { void submit(); }}
              disabled={runner.busy || blocked || partial || !said || ids.length === 0 || (needsDeliberateWords && !accept)}>
        {runner.busy ? 'Switching…' : blocked ? 'Close the holes first' : !said ? 'Say why, in a sentence'
          : needsDeliberateWords && !accept ? 'Confirm the price change is intended' : 'Switch the company to the catalogue'}
      </Button>
    </Card>
  );
}
